import {
	Form,
	FormInstance,
	Input,
	Typography,
	Checkbox,
	Button,
	Grid,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { PredefinedReasonCategory, ReviewAction } from '../../shared/Constants';
import { useCallback, useEffect, useState } from 'react';
import PredefinedReasonsDropdown from '../../components/PredefinedReasonsDropdown';
import {
	Application,
	Feedback,
	UserRole,
	OtherReferenceFeedback,
} from '../../shared/VolunteerApplicationServiceTypes';
import Icon from '../../components/ui-components/Icon';
import RedDivideCircle from '../../assets/images/red-divide-circle.svg';
import DivideCircle from '../../assets/images/divide-circle.svg';
import GreenTick from '../../assets/images/green-tick.svg';
import GrayTick from '../../assets/images/gray-tick.svg';
import BlueTick from '../../assets/images/blue-tick.svg';
import useUserSession from '../../hooks/useUserSession';

const { Text } = Typography;
const { useBreakpoint } = Grid;
export interface RecommendationComponentUIConfig {
	commentsMandatory?: boolean;
	commentsLabel?: string;
	commentsPlaceholder?: string;
	form?: FormInstance;
	category?: string;
	displayComments?: boolean;
	disableReview?: boolean;
	hideReasons?: boolean;
	disabled?: boolean;
	displayForwardToCareTeam?: boolean;
	application?: Application | undefined;
	recommendationBy?: UserRole;
	showForwardToCareTeam?: boolean;
}

export const RecommendationComponent = ({
	commentsMandatory,
	commentsLabel = 'comments',
	commentsPlaceholder,
	form,
	category,
	displayComments,
	disableReview = false,
	hideReasons,
	disabled,
	displayForwardToCareTeam = true,
	application,
	recommendationBy,
	showForwardToCareTeam,
}: RecommendationComponentUIConfig) => {
	const { t, i18n } = useTranslation();
	const [commentsRequired, setCommentsRequired] = useState<boolean>(
		commentsMandatory ?? false
	);
	const [lengthyLanguage, setLengthyLanguage] = useState<boolean>();

	const screens = useBreakpoint();
	const { selectedRole } = useUserSession();
	const isAdmin = selectedRole === UserRole.SNP_ADMIN;
	const isMobile = !screens.sm;
	const isStackVertical = lengthyLanguage && isMobile;
	const [reasonDropdownVisible, setReasonDropdownVisible] = useState<boolean>();
	const values = Form.useWatch<Feedback | OtherReferenceFeedback>([], form);
	const defaultNGF = category === PredefinedReasonCategory.SKIP;
	const [selectedReview, setSelectedReview] = useState<string | null>(null);
	const reviewValue = Form.useWatch('review', form) as string;
	const shouldCareTeamReview = Form.useWatch(
		'shouldCareTeamReview',
		form
	) as boolean;
	const showApproverRevertedForwardToCareTeamComments =
		application?.approverRevertedForwardedToCareTeam !== false &&
		shouldCareTeamReview === false &&
		application?.approverForwardedToCareTeam;

	useEffect(() => {
		if (reviewValue) {
			setSelectedReview(reviewValue);
		}
		if (defaultNGF && form) {
			setSelectedReview(ReviewAction.REJECT);
			form.setFieldsValue({ review: ReviewAction.REJECT });
		}
	}, [reviewValue, defaultNGF, form]);

	useEffect(() => {
		// Recommendation by ZC to Care Team
		if (form && recommendationBy === UserRole.ZC) {
			form?.setFieldsValue({
				shouldCareTeamReview: application?.zcForwardedToCareTeam,
			});

			// Recommendation by Approver to Care Team
		} else if (form && recommendationBy === UserRole.APPROVER) {
			const isApproverRecommendationEmpty =
				!application?.approverRecommendation ||
				(application?.approverRecommendation &&
					Object.keys(application.approverRecommendation).length === 0);
			form?.setFieldsValue({
				shouldCareTeamReview: application?.approverForwardedToCareTeam,
			});

			if (application?.approverRevertedForwardedToCareTeam === true) {
				form?.setFieldsValue({
					shouldCareTeamReview: false,
				});
			} else if (application?.approverRevertedForwardedToCareTeam === false) {
				form?.setFieldsValue({
					shouldCareTeamReview: isApproverRecommendationEmpty ? false : true,
				});
			}
		}
	}, [
		application?.approverForwardedToCareTeam,
		application?.zcForwardedToCareTeam,
		displayForwardToCareTeam,
		form,
		isAdmin,
		recommendationBy,
		application?.approverRecommendation,
		application?.approverRevertedForwardedToCareTeam,
	]);

	useEffect(() => {
		const lengthyLanguages =
			recommendationBy === UserRole.ZC
				? ['en', 'ta', 'ma', 'ka', 'te', 'hi']
				: ['ta', 'ma', 'ka', 'te'];

		setLengthyLanguage(lengthyLanguages.includes(i18n.language));
	}, [i18n.language, recommendationBy]);

	useEffect(() => {
		const isDisapproved =
			values?.review === ReviewAction.REJECT ||
			category === PredefinedReasonCategory.SKIP;

		if (commentsMandatory) {
			setCommentsRequired(true);
		} else {
			setCommentsRequired(isDisapproved);
		}

		if (hideReasons) {
			setReasonDropdownVisible(false);
		} else {
			if (values?.review || category === PredefinedReasonCategory.SKIP) {
				setReasonDropdownVisible(isDisapproved);
			} else {
				setReasonDropdownVisible(false);
			}
		}
	}, [commentsMandatory, values, category, hideReasons]);

	const handleReviewChange = useCallback(
		(value: string) => {
			setSelectedReview(value);
			if (form) {
				form.setFieldsValue({ review: value });
				if (
					value === ReviewAction.APPROVE ||
					value === ReviewAction.APPROVE_IDK_CANDIDATE
				) {
					form.setFieldsValue({ predefinedReasons: undefined });
					setReasonDropdownVisible(false);
				} else if (value === ReviewAction.REJECT && !hideReasons) {
					setReasonDropdownVisible(true);
				}
			}

			if (commentsMandatory) {
				setCommentsRequired(true);
				return;
			}

			if (value && value !== ReviewAction.APPROVE) {
				setCommentsRequired(true);
				return;
			}

			setCommentsRequired(false);
		},
		[commentsMandatory, form, hideReasons]
	);

	const buttonProps =
		'w-44 h-9 font-sans font-bold text-[13px] text-gray-650 hover:!text-gray-650';

	return (
		<div className="flex flex-row items-start sm:mt-0 md:mt-4">
			<div className="flex flex-col w-full">
				{displayComments && (
					<>
						<Text className="flex flex-wrap text-[13px] md:text-[14px]">
							<span>
								{t(commentsLabel)}
								{commentsRequired && (
									<span className="text-red-500">
										&nbsp;<b>*</b>
									</span>
								)}
							</span>
						</Text>
						<Form.Item
							className="font-sans font-bold"
							name={['comments']}
							rules={[
								{
									required: commentsRequired,
									message: <span>{t('required')}</span>,
								},
								{
									min: 10,
									message: <span>{t('commentsShouldBeClear')}</span>,
								},
							]}
						>
							<Input.TextArea
								showCount
								rows={4}
								placeholder={
									commentsRequired
										? commentsPlaceholder
											? `${t(commentsPlaceholder)}`
											: `${t('commentsAreMandatoryNGF')}`
										: ''
								}
								className="font-sans text-sm font-normal"
								disabled={disabled}
							/>
						</Form.Item>
					</>
				)}
				{(recommendationBy === UserRole.ZC ||
					((recommendationBy === UserRole.APPROVER || isAdmin) &&
						displayForwardToCareTeam)) && (
					<>
						{!showForwardToCareTeam && (
							<Form.Item name="shouldCareTeamReview" valuePropName="checked">
								<Checkbox disabled={disabled}>
									{t('forwardItToCareTeam')}
								</Checkbox>
							</Form.Item>
						)}
						{/* Show approver reverted textbox only if the Approver Reverted Forwarded to Care Team is true and user unchecks checkbox */}
						{showApproverRevertedForwardToCareTeamComments && (
							<>
								<Text className="flex flex-wrap text-[13px] md:text-[14px]">
									<span>
										{t('approverRevertedForwardedToCareTeamComments')}
										{commentsRequired && (
											<span className="text-red-500">
												&nbsp;<b>*</b>
											</span>
										)}
									</span>
								</Text>

								<Form.Item
									className="font-sans font-bold"
									name={['approverRevertedForwardedToCareTeamComments']}
									rules={[
										{
											required: commentsRequired,
											message: <span>{t('required')}</span>,
										},
										{
											min: 10,
											message: <span>{t('commentsShouldBeClear')}</span>,
										},
									]}
								>
									<Input.TextArea
										showCount
										rows={4}
										placeholder={
											commentsRequired
												? commentsPlaceholder
													? `${t(commentsPlaceholder)}`
													: `${t('commentsAreMandatoryNGF')}`
												: ''
										}
										className="font-sans text-sm font-normal"
										disabled={disabled}
									/>
								</Form.Item>
							</>
						)}
					</>
				)}
				<Form.Item
					name={['review']}
					rules={[
						{
							required: true,
							message: <span>{t('answerIsRequired')}</span>,
						},
					]}
				>
					{recommendationBy === UserRole.ZC && (
						<div className="flex justify-center mb-4">
							<Button
								icon={
									<Icon
										src={
											selectedReview === ReviewAction.APPROVE_IDK_CANDIDATE
												? BlueTick
												: GrayTick
										}
										width={'w-5'}
										height={'h-5'}
									/>
								}
								onClick={() =>
									handleReviewChange(ReviewAction.APPROVE_IDK_CANDIDATE)
								}
								iconPosition="end"
								className={`
									w-fit whitespace-normal border-solid border border-approve-border-blue
									${buttonProps} 
									hover:!text-gray-650 hover:!bg-approve-blue 
									${
										selectedReview === ReviewAction.APPROVE_IDK_CANDIDATE
											? '!bg-approve-blue'
											: 'bg-transparent'
									} 
									${
										defaultNGF || disableReview || disabled
											? 'cursor-not-allowed'
											: 'cursor-pointer'
									}
									${
										(defaultNGF || disableReview || disabled) &&
										selectedReview !== ReviewAction.APPROVE_IDK_CANDIDATE
											? 'opacity-50'
											: 'opacity-100'
									}
									w-fit
								`}
								disabled={defaultNGF || disableReview || disabled}
							>
								{t('approveIDoNotKnowTheCandidate')}
							</Button>
						</div>
					)}

					<div
						className={`${
							isStackVertical
								? 'flex flex-col items-center'
								: 'flex justify-center'
						} gap-4`}
					>
						<Button
							icon={
								<Icon
									src={
										selectedReview === ReviewAction.REJECT
											? RedDivideCircle
											: DivideCircle
									}
									width={'w-5'}
									height={'h-5'}
								/>
							}
							disabled={disableReview || disabled}
							onClick={() => handleReviewChange(ReviewAction.REJECT)}
							iconPosition="end"
							className={`
								${buttonProps} 
								border border-solid !border-ngf-border-orange
								hover:!bg-ngf-orange 
								${selectedReview === ReviewAction.REJECT ? '!bg-ngf-orange' : 'bg-transparent'} 
								${
									(disableReview || disabled) &&
									selectedReview !== ReviewAction.REJECT
										? 'opacity-50'
										: 'opacity-100'
								}
								${disableReview || disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
								w-fit
							`}
						>
							{t('notGoingForward')}
						</Button>
						<Button
							icon={
								<Icon
									src={
										selectedReview === ReviewAction.APPROVE
											? GreenTick
											: GrayTick
									}
									width={'w-5'}
									height={'h-5'}
								/>
							}
							onClick={() => handleReviewChange(ReviewAction.APPROVE)}
							iconPosition="end"
							className={`border border-solid !border-approve-border-green
								${buttonProps} 
								hover:!text-gray-650 hover:!bg-approve-green 
								${
									selectedReview === ReviewAction.APPROVE
										? '!bg-approve-green'
										: 'bg-transparent'
								}
								${
									(defaultNGF || disableReview || disabled) &&
									selectedReview !== ReviewAction.APPROVE
										? 'opacity-50'
										: 'opacity-100'
								}
								${
									defaultNGF || disableReview || disabled
										? 'cursor-not-allowed'
										: 'cursor-pointer'
								}
							`}
							disabled={defaultNGF || disableReview || disabled}
						>
							{t('approve')}
						</Button>
					</div>
				</Form.Item>
				{reasonDropdownVisible && (
					<>
						<Text>
							{t('reason')}{' '}
							{
								<span className="text-red-500">
									<b>*</b>
								</span>
							}
						</Text>
						<Form.Item
							name={['predefinedReasons']}
							rules={[
								{
									required: true,
									message: <span>{t('required')}</span>,
								},
							]}
						>
							<PredefinedReasonsDropdown
								category={category}
								disabled={disableReview || disabled}
							/>
						</Form.Item>
					</>
				)}
			</div>
		</div>
	);
};
